import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, Chip, Stack, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import Drawer from '@mui/material/Drawer';
import React from 'react';
import { BooleanParam, useQueryParam } from 'use-query-params';

import ActionsMenu from 'components/ActionsMenu';
import { filtersDrawerWidth, overviewDrawerWidth } from 'components/atoms/FlexContainers/FlexContainers';
import { useRowSelectionContext } from 'components/atoms/RowSelectionContext';
import { useCopilotDrawerState } from 'components/Copilot';
import { getViewerOverlaySurfaceStyles } from 'components/theme/theme';
import { size } from 'lodash';
import { useCurrentStateCases } from 'utils/useCurrentStateCases';
import { useBulkEditControl } from './ProceduresPage/DataGrids/useBulkEditControl';
import { ProcedurePagination } from './ProceduresPage/ProcedurePagination';
import SelectedSlidesCopyButton from './ProceduresPage/SelectedSlidesCopyButton';
import { useCasesViewMode } from './ProceduresPage/useCasesViewMode';

interface Props {
  displayProcedureModeToggle?: boolean;
  workflowStatus?: React.ReactNode;
}

const SelectedCardsDrawer: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  displayProcedureModeToggle,
  workflowStatus,
}) => {
  const theme = useTheme();
  const keepFooterControlsInline = useMediaQuery(theme.breakpoints.up('sm'));
  const compactFooterActions = useMediaQuery(theme.breakpoints.down('lg'));
  const [slidesMode] = useQueryParam('slidesMode', BooleanParam);
  const { casesViewMode } = useCasesViewMode();
  const procedureViewMode = displayProcedureModeToggle ? casesViewMode : 'card';
  const selectedItemsType = slidesMode ? 'slides' : 'cases';
  const { totalCasesCount, totalSlidesCount, currentStains } = useCurrentStateCases();
  const totalRowsCount = slidesMode ? totalSlidesCount : totalCasesCount;

  const { selectedRows, selectionMode, omittedRows, someSelected } = useRowSelectionContext();

  const totalSelectedRows = selectionMode === 'select' ? selectedRows.length : totalRowsCount - omittedRows.length;

  const { bulkEditMode, setBulkEditMode, undoBulkEdits } = useBulkEditControl();
  const itemsStr =
    totalSelectedRows > 1 ? selectedItemsType : selectedItemsType.substring(0, size(selectedItemsType) - 1);
  const { copilotDrawerOpen, copilotDrawerWidth } = useCopilotDrawerState();
  const copilotDrawerLayoutTransitionDuration = `var(--copilot-drawer-layout-transition-duration, ${theme.transitions.duration.enteringScreen}ms)`;
  const [advancedSearchDrawerOpen] = useQueryParam('advancedSearchDrawerOpen', BooleanParam);

  const totalRows = slidesMode ? totalSlidesCount : totalCasesCount;
  const isCardView = procedureViewMode === 'card';
  const reservedLeftWidth = advancedSearchDrawerOpen ? filtersDrawerWidth : 0;
  const reservedRightWidth =
    (copilotDrawerOpen ? copilotDrawerWidth : 0) + (bulkEditMode && isCardView ? overviewDrawerWidth : 0);

  const areSomeSelected = someSelected(totalRows);
  const selectionSummary = `${totalSelectedRows} / ${totalRows} ${slidesMode ? 'slide' : 'case'}${
    totalRows === 1 ? '' : 's'
  } selected`;
  const editingSummary = `Editing ${totalSelectedRows} / ${totalRows} ${slidesMode ? 'slide' : 'case'}${
    totalRows === 1 ? '' : 's'
  }`;
  const compactSelectionSummary = `${totalSelectedRows} selected`;
  const compactEditingSummary = `Editing ${totalSelectedRows}`;
  const statusChipLabel = compactFooterActions
    ? bulkEditMode
      ? compactEditingSummary
      : compactSelectionSummary
    : bulkEditMode
    ? editingSummary
    : selectionSummary;

  if (procedureViewMode === 'table') {
    return null;
  }

  return (
    <Drawer
      open
      data-cy="edit-drawer"
      variant="persistent"
      anchor="bottom"
      PaperProps={{
        sx: {
          zIndex: theme.zIndex.modal - 1,
          ...getViewerOverlaySurfaceStyles(theme),
          borderTopLeftRadius: 18,
          borderTopRightRadius: 18,
          padding: { xs: 1.5, sm: 2 },
          paddingBottom: `max(${theme.spacing(1.5)}, calc(${theme.spacing(1.5)} + env(safe-area-inset-bottom)))`,
          color: theme.palette.text.primary,
          overflow: 'visible',
          borderTop: `1px solid ${theme.palette.mode === 'light' ? theme.palette.grey[200] : theme.palette.grey[800]}`,
          left: reservedLeftWidth ? `${reservedLeftWidth}px` : 0,
          right: reservedRightWidth ? `${reservedRightWidth}px` : 0,
          width:
            reservedLeftWidth || reservedRightWidth
              ? `calc(100% - ${reservedLeftWidth + reservedRightWidth}px)`
              : '100%',
          transition: [
            `width ${copilotDrawerLayoutTransitionDuration} ${theme.transitions.easing.easeOut}`,
            `left ${copilotDrawerLayoutTransitionDuration} ${theme.transitions.easing.easeOut}`,
            `right ${copilotDrawerLayoutTransitionDuration} ${theme.transitions.easing.easeOut}`,
          ].join(', '),
        },
      }}
    >
      {workflowStatus && (
        <Box
          data-testid="dataset-workflow-status-slot"
          sx={{
            position: 'absolute',
            bottom: `calc(100% + ${theme.spacing(1)})`,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            justifyContent: 'center',
            width: 'max-content',
            maxWidth: `calc(100% - ${theme.spacing(2)})`,
          }}
        >
          {workflowStatus}
        </Box>
      )}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={compactFooterActions ? 1 : 2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        sx={{
          width: '100%',
          overflowX: keepFooterControlsInline ? 'auto' : 'visible',
          scrollbarWidth: keepFooterControlsInline ? 'thin' : undefined,
          '&::-webkit-scrollbar': {
            height: keepFooterControlsInline ? 6 : 0,
          },
        }}
      >
        <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
          <ProcedurePagination
            totalSlides={totalSlidesCount}
            totalCases={totalCasesCount}
            showRowsPerPageSelector
            alignRowsPerPageLeft
          />
        </Box>

        <Stack
          direction="row"
          spacing={compactFooterActions ? 1 : 2}
          alignItems="center"
          justifyContent="flex-end"
          flexWrap="nowrap"
          sx={{ rowGap: 1, flex: '0 0 auto', minWidth: 0 }}
        >
          {isCardView ? (
            <>
              {areSomeSelected && (
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                  <Chip
                    color={bulkEditMode ? 'warning' : 'primary'}
                    variant="filled"
                    label={statusChipLabel}
                    data-testid="selected-cards-status-chip"
                    sx={{
                      fontWeight: 600,
                      maxWidth: compactFooterActions ? 160 : '100%',
                      '& .MuiChip-label': {
                        px: 1.25,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      },
                    }}
                  />
                  {slidesMode ? (
                    <SelectedSlidesCopyButton
                      selectedRows={selectedRows}
                      omittedRows={omittedRows}
                      selectionMode={selectionMode}
                      color={bulkEditMode ? 'warning' : 'primary'}
                    />
                  ) : null}
                </Stack>
              )}
              <Box>
                <Tooltip title={areSomeSelected ? '' : `Select ${itemsStr} to edit`} arrow>
                  <span>
                    <Button
                      endIcon={compactFooterActions ? undefined : <EditIcon />}
                      variant="contained"
                      size="small"
                      data-cy="edit-selected-items-button"
                      disabled={!areSomeSelected}
                      sx={{
                        minWidth: 0,
                        px: compactFooterActions ? 1.25 : undefined,
                        whiteSpace: 'nowrap',
                      }}
                      onClick={() => {
                        if (bulkEditMode) {
                          undoBulkEdits();
                        }
                        setBulkEditMode(!bulkEditMode);
                      }}
                    >
                      {compactFooterActions
                        ? bulkEditMode
                          ? 'Discard'
                          : 'Edit'
                        : bulkEditMode
                        ? 'Discard Changes'
                        : 'Edit'}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
              <Box>
                <ActionsMenu
                  totalRows={totalSelectedRows}
                  currentStains={currentStains}
                  placement="top-end"
                  compact={compactFooterActions}
                />
              </Box>
            </>
          ) : null}
        </Stack>
      </Stack>
    </Drawer>
  );
};

export default SelectedCardsDrawer;
